if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this._syncing = false;
        this._mainInView = true;
        this._footerInView = false;
        this._ioMain = null;
        this._ioFooter = null;
        this._scrollFallback = null;
        this._mainQtyInput = null;
        this._onMainQtyInput = null;
        this._unsubVariant = null;
        this._unsubQty = null;
        this._onSelectChange = this._onSelectChange.bind(this);
        this._onQtyChange = this._onQtyChange.bind(this);
        this._onAddToCart = this._onAddToCart.bind(this);
      }

      connectedCallback() {
        if (this.__connected) return;
        this.__connected = true;

        this.sectionId = this.dataset.sectionId || null;
        this.main =
          (this.sectionId && document.querySelector(`product-information[data-section="${this.sectionId}"]`)) ||
          (this.sectionId && document.getElementById(`ProductInformation-${this.sectionId}`)) ||
          null;

        this.els = {
          selects: Array.from(this.querySelectorAll('select[data-sticky-option-index]')),
          qtyInput: this.querySelector('.quantity__input[name="quantity"]'),
          addBtn: this.querySelector('[ref="addToCartButton"]') || this.querySelector('.sticky-add-to-cart__button'),
          addBtnText: this.querySelector('[data-sticky-add-to-cart-text]'),
          qtyNumber: this.querySelector('[ref="quantityNumber"]'),
          variantTitle: this.querySelector('[data-sticky="variant-title"]'),
          price: this.querySelector('[data-sticky="price-display"]'),
          image: this.querySelector('[data-sticky="product-image"]'),
          bar: this.querySelector('.sticky-add-to-cart__bar'),
        };

        this.els.selects.forEach((sel) => sel.addEventListener('change', this._onSelectChange));
        this.els.qtyInput?.addEventListener('change', this._onQtyChange);
        this.els.qtyInput?.addEventListener('input', this._onQtyChange);
        this.els.addBtn?.addEventListener('click', this._onAddToCart);

        // Default hidden to avoid flash; observers will decide final state.
        this._setVisible(false);
        this._setupVisibilityObservers();
        // Bind quantity sync immediately (don't wait for the first variant sync tick).
        this._bindMainQtyListeners();
        this._syncQuantityFromMain();

        // Keep sticky in sync when the main product changes variants / quantity rules
        this._unsubVariant = theme.subscribe(theme.PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!data?.sectionId) return;
          if (this.sectionId && String(data.sectionId) !== String(this.sectionId)) return;
          this._syncFromMain(data.variant);
        });
        this._unsubQty = theme.subscribe(theme.PUB_SUB_EVENTS.quantityUpdate, () => this._syncQuantityFromMain());

        // Initial sync (after main components hydrate)
        requestAnimationFrame(() => {
          this._syncFromMain();
          this._updateVisibility();
          this._getColorPickerFromMain();
        });
      }

      disconnectedCallback() {
        this._unsubVariant?.();
        this._unsubQty?.();
        this._unsubVariant = null;
        this._unsubQty = null;

        this.els?.selects?.forEach((sel) => sel.removeEventListener('change', this._onSelectChange));
        this.els?.qtyInput?.removeEventListener('change', this._onQtyChange);
        this.els?.qtyInput?.removeEventListener('input', this._onQtyChange);
        this.els?.addBtn?.removeEventListener('click', this._onAddToCart);

        this._ioMain?.disconnect?.();
        this._ioFooter?.disconnect?.();
        this._ioMain = null;
        this._ioFooter = null;
        if (this._scrollFallback) {
          window.removeEventListener('scroll', this._scrollFallback, { passive: true });
          window.removeEventListener('resize', this._scrollFallback);
          this._scrollFallback = null;
        }

        this._unbindMainQtyListeners();
        this.__connected = false;
      }

      _unbindMainQtyListeners() {
        if (this._mainQtyInput && this._onMainQtyInput) {
          this._mainQtyInput.removeEventListener('input', this._onMainQtyInput);
          this._mainQtyInput.removeEventListener('change', this._onMainQtyInput);
        }
        this._mainQtyInput = null;
        this._onMainQtyInput = null;
      }

      _bindMainQtyListeners() {
        const mainQty = this._getMainQtyInput();
        if (!mainQty) {
          this._unbindMainQtyListeners();
          return;
        }

        // Re-bind if the element changed (DOM swapped after variant changes)
        if (this._mainQtyInput === mainQty && this._onMainQtyInput) return;
        this._unbindMainQtyListeners();

        this._mainQtyInput = mainQty;
        this._onMainQtyInput = () => {
          if (this._syncing) return;
          this._syncQuantityFromMain();
        };
        this._mainQtyInput.addEventListener('input', this._onMainQtyInput);
        this._mainQtyInput.addEventListener('change', this._onMainQtyInput);
      }

      _setVisible(visible) {
        this.classList.toggle('is-visible', !!visible);
      }

      _updateVisibility() {
        const visible = !this._mainInView && !this._footerInView;
        this._setVisible(visible);
      }

      _setupVisibilityObservers() {
        // Prefer section-scoped main content when possible
        const mainContent =
          this.main?.querySelector('.main-product-information__content') ||
          document.querySelector('.main-product-information__content');

        const footer = document.querySelector('footer-global');

        // If we can't find main content, don't block the sticky from showing.
        this._mainInView = mainContent ? true : false;
        this._footerInView = false;

        if ('IntersectionObserver' in window) {
          if (mainContent) {
            this._ioMain = new IntersectionObserver(
              (entries) => {
                const entry = entries[0];
                this._mainInView = !!entry?.isIntersecting;
                this._updateVisibility();
              },
              { threshold: 0.01 }
            );
            this._ioMain.observe(mainContent);
          }

          if (footer) {
            this._ioFooter = new IntersectionObserver(
              (entries) => {
                const entry = entries[0];
                this._footerInView = !!entry?.isIntersecting;
                this._updateVisibility();
              },
              { threshold: 0.01 }
            );
            this._ioFooter.observe(footer);
          }

          return;
        }

        // Fallback for older browsers
        this._scrollFallback = () => {
          if (mainContent) {
            const r = mainContent.getBoundingClientRect();
            this._mainInView = r.bottom > 0 && r.top < window.innerHeight;
          } else {
            this._mainInView = false;
          }

          if (footer) {
            const r2 = footer.getBoundingClientRect();
            this._footerInView = r2.top < window.innerHeight && r2.bottom > 0;
          } else {
            this._footerInView = false;
          }

          this._updateVisibility();
        };
        window.addEventListener('scroll', this._scrollFallback, { passive: true });
        window.addEventListener('resize', this._scrollFallback);
        this._scrollFallback();
      }

      _getVariantFromMain() {
        const root = this.main || document;

        const jsonEl =
          root.querySelector('[data-selected-variant]') ||
          root.querySelector('variant-picker script[type="application/json"]') ||
          root.querySelector('variant-swatches-picker script[type="application/json"]');

        const raw = jsonEl?.textContent || jsonEl?.innerHTML;

        if (!raw) return null;
        try {
          return JSON.parse(raw);
        } catch {
          return null;
        }
      }

      _getMainVariantPicker() {
        return this.main?.querySelector('variant-picker') || null;
      }

      _getMainQtyInput() {
        return this.main?.querySelector('.quantity__input[name="quantity"]') || null;
      }

      _getMainPriceEl() {
        return this.main?.querySelector('product-price') || null;
      }

      _getMainActiveImageUrl() {
        if (!this.sectionId) return null;
        const gallery = document.getElementById(`ProductMediaGallery-${this.sectionId}`);
        if (!gallery) return null;
        const active =
          gallery.querySelector('.is-active img') ||
          gallery.querySelector('.swiper-slide.swiper-slide-active img') ||
          gallery.querySelector('img');
        if (!active) return null;
        return active.currentSrc || active.getAttribute('src') || null;
      }

      _updateQtyBadge(qty) {
        const q = Number.isFinite(qty) ? qty : 1;
        if (this.els.qtyNumber) this.els.qtyNumber.textContent = String(q);
      }

      _syncQuantityFromMain() {
        const mainQty = this._getMainQtyInput();
        const stickyQty = this.els.qtyInput;
        if (!mainQty || !stickyQty) return;

        this._syncing = true;
        const attrs = ['min', 'max', 'step', 'data-min', 'data-max', 'data-cart-quantity'];
        attrs.forEach((attr) => {
          const v = mainQty.getAttribute(attr);
          if (v === null) stickyQty.removeAttribute(attr);
          else stickyQty.setAttribute(attr, v);
        });

        stickyQty.value = String(mainQty.value || '1');
        stickyQty.setAttribute('value', stickyQty.value);
        this._updateQtyBadge(parseInt(stickyQty.value) || 1);
        this._syncing = false;
      }

      _syncFromMain(variantOverride = null) {
        // Re-grab main container (it can be replaced in some swaps)
        if (!this.main && this.sectionId) {
          this.main =
            document.querySelector(`product-information[data-section="${this.sectionId}"]`) ||
            document.getElementById(`ProductInformation-${this.sectionId}`) ||
            null;
        }

        const variant = variantOverride || this._getVariantFromMain();

        if (variant) {
          // Update selects to match the current variant
          const opts = variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean);
          this._syncing = true;
          this.els.selects.forEach((sel) => {
            const idx = parseInt(sel.dataset.stickyOptionIndex || '0');
            const next = opts[idx];
            if (!next) return;
            sel.value = next;
            Array.from(sel.options).forEach((o) => o.toggleAttribute('selected', o.value === sel.value));
          });
          this._syncing = false;

          // Availability + title
          if (this.els.addBtn) this.els.addBtn.toggleAttribute('disabled', variant.available === false);
          if (this.els.addBtnText) {
            const strings = window.variantStrings || {};

            const label = variant.available === false ? (strings.soldOut || 'Sold out') : (strings.addToCart || 'Add to cart');
            this.els.addBtnText.textContent = label;
            this.els.addBtn.setAttribute('aria-label', label);
          }
          if (this.els.variantTitle) {
            const title = variant.title && variant.title !== 'Default Title' ? variant.title : (this.dataset.defaultVariantTitle || '');
            this.els.variantTitle.textContent = title;
          }
        } else {
          // No variant resolved => unavailable

          if (this.els.addBtn) this.els.addBtn.toggleAttribute('disabled', true);
          if (this.els.addBtnText) {
            const strings = window.variantStrings || {};
            const label = strings.unavailable || 'Unavailable';
            this.els.addBtnText.textContent = label;
            this.els.addBtn?.setAttribute('aria-label', label);
          }
        }

        // Price: mirror from main product-price markup to preserve formatting/sale/compare-at
        const mainPrice = this._getMainPriceEl();
        if (mainPrice && this.els.price) {
          this.els.price.innerHTML = mainPrice.innerHTML;
        }

        // Image: mirror from currently active media
        const variantImgUrl =
          variant?.featured_image?.src ||
          variant?.featured_image?.url ||
          variant?.featured_image ||
          null;
        const imgUrl = variantImgUrl || this._getMainActiveImageUrl();
        if (imgUrl && this.els.image) {
          this.els.image.setAttribute('src', imgUrl);
          this.els.image.setAttribute('srcset', imgUrl);
        }

        // Quantity: mirror rules + value from main
        this._bindMainQtyListeners();
        this._syncQuantityFromMain();
      }

      _syncVariantToMain(changedIndex) {
        const mainVP = this._getMainVariantPicker();
        if (!mainVP) return;

        const safeEscape = (s) => {
          try {
            return CSS.escape(s);
          } catch {
            return String(s).replace(/"/g, '\\"');
          }
        };

        const mainSelects = Array.from(mainVP.querySelectorAll('select.variant-option__select'));
        if (mainSelects.length) {
          this.els.selects.forEach((stickySel) => {
            const idx = parseInt(stickySel.dataset.stickyOptionIndex || '0');
            const mainSel = mainSelects[idx];
            if (!mainSel) return;

            // Prefer matching by option value id if available, else by value text
            const stickyOpt = stickySel.selectedOptions?.[0];
            const stickyOptionValueId = stickyOpt?.dataset?.optionValueId;

            if (stickyOptionValueId) {
              const match = Array.from(mainSel.options).find((o) => o.dataset.optionValueId === stickyOptionValueId);
              if (match) {
                mainSel.value = match.value;
                Array.from(mainSel.options).forEach((o) => o.toggleAttribute('selected', o === match));
                return;
              }
            }

            mainSel.value = stickySel.value;
            Array.from(mainSel.options).forEach((o) => o.toggleAttribute('selected', o.value === mainSel.value));
          });

          const idx = Number.isFinite(changedIndex) ? changedIndex : 0;
          const fire = mainSelects[idx] || mainSelects[0];
          fire?.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        // Radio buttons variant style
        this.els.selects.forEach((stickySel) => {
          const idx = parseInt(stickySel.dataset.stickyOptionIndex || '0');
          const stickyOpt = stickySel.selectedOptions?.[0];
          const stickyOptionValueId = stickyOpt?.dataset?.optionValueId;

          const byId = stickyOptionValueId
            ? mainVP.querySelector(`input[type="radio"][data-fieldset-index="${idx}"][data-option-value-id="${safeEscape(stickyOptionValueId)}"]`)
            : null;
          const byValue = mainVP.querySelector(`input[type="radio"][data-fieldset-index="${idx}"][value="${safeEscape(stickySel.value)}"]`);
          const input = byId || byValue;
          if (!input) return;
          input.checked = true;
        });

        const idx = Number.isFinite(changedIndex) ? changedIndex : 0;
        const stickySel = this.els.selects[idx] || this.els.selects[0];
        if (stickySel) {
          const stickyOpt = stickySel.selectedOptions?.[0];
          const stickyOptionValueId = stickyOpt?.dataset?.optionValueId;
          const input =
            (stickyOptionValueId &&
              mainVP.querySelector(
                `input[type="radio"][data-fieldset-index="${idx}"][data-option-value-id="${safeEscape(stickyOptionValueId)}"]`
              )) ||
            mainVP.querySelector(`input[type="radio"][data-fieldset-index="${idx}"][value="${safeEscape(stickySel.value)}"]`);
          input?.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      _onSelectChange(e) {
        if (this._syncing) return;
        const idx = parseInt(e.target?.dataset?.stickyOptionIndex || '0');
        this._syncVariantToMain(Number.isFinite(idx) ? idx : 0);
      }

      _onQtyChange() {
        if (this._syncing) return;
        const stickyQty = this.els.qtyInput;
        const mainQty = this._getMainQtyInput();
        if (!stickyQty || !mainQty) return;

        const n = parseInt(stickyQty.value);
        const qty = Number.isFinite(n) && n > 0 ? n : 1;

        this._syncing = true;
        mainQty.value = String(qty);
        mainQty.setAttribute('value', String(qty));
        // Some quantity components react on `input`, others on `change` — fire both.
        mainQty.dispatchEvent(new Event('input', { bubbles: true }));
        mainQty.dispatchEvent(new Event('change', { bubbles: true }));
        this._syncing = false;

        this._updateQtyBadge(qty);
      }

      _onAddToCart(e) {
        e.preventDefault();
        const mainForm = this.main?.querySelector('product-form form');
        if (!mainForm) return;

        // Ensure main quantity equals sticky quantity before submitting
        this._onQtyChange();

        if (typeof mainForm.requestSubmit === 'function') {
          mainForm.requestSubmit();
        } else {
          mainForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      }

      _getColorPickerFromMain() {
        const stickyPrice = this.els.price;
        const productPrice = this.main.querySelector('product-price');

        if (productPrice && stickyPrice) {
          const computedStyle = getComputedStyle(productPrice);

          const colorSale = computedStyle.getPropertyValue('--color-sale').trim();
          const color = computedStyle.getPropertyValue('--color').trim();

          if (colorSale) {
            stickyPrice.style.setProperty('--color-sale', colorSale);
          }

          if (color) {
            stickyPrice.style.setProperty('--color', color);
          }
        }
      }
    }
  );
}